// FILE: src/components/modals/NotificationsModal.jsx

import React, { useState } from 'react';
import { Bell, Package, Gift, AlertCircle, CheckCircle, Clock, Tag, Ticket, ExternalLink, Copy, ArrowLeft } from 'lucide-react';

const NotificationsModal = ({
  isOpen,
  onClose,
  notifications,
  setNotifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onDeleteNotification,
  onClearAll,
  onViewOrder,
  appTheme = 'blue',
  isDesktop = false,
}) => {
  const headerStyle = appTheme === 'white'
    ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
    : { background: 'var(--header-gradient)', color: 'var(--text-light)' };
  const [copiedCode, setCopiedCode] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  const copyCouponCode = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code).catch(() => {
      const el = document.createElement('textarea');
      el.value = code;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (!isOpen) return null;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order':
        return <Package size={20} className="text-blue-600" />;
      case 'offer':
        return <Gift size={20} className="text-green-600" />;
      case 'promotion':
        return <Tag size={20} className="text-purple-600" />;
      case 'coupon':
        return <Ticket size={20} className="text-pink-600" />;
      case 'app_update':
        return <Bell size={20} className="text-indigo-600" />;
      case 'payment':
        return <CheckCircle size={20} className="text-green-600" />;
      case 'alert':
        return <AlertCircle size={20} className="text-red-600" />;
      case 'reminder':
        return <Clock size={20} className="text-orange-600" />;
      default:
        return <Bell size={20} className="text-gray-600" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'order':
        return 'bg-blue-50 border-blue-200';
      case 'offer':
        return 'bg-green-50 border-green-200';
      case 'promotion':
        return 'bg-purple-50 border-purple-200';
      case 'coupon':
        return 'bg-pink-50 border-pink-200';
      case 'app_update':
        return 'bg-indigo-50 border-indigo-200';
      case 'payment':
        return 'bg-green-50 border-green-200';
      case 'alert':
        return 'bg-red-50 border-red-200';
      case 'reminder':
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const markAsRead = async (notificationId) => {
    if (actionInProgress) return;
    try {
      setActionInProgress(true);
      if (onMarkAsRead) {
        await onMarkAsRead(notificationId);
        return;
      }
      setNotifications(notifications.map(notif =>
        notif.id === notificationId ? { ...notif, unread: false } : notif
      ));
    } finally {
      setActionInProgress(false);
    }
  };

  const markAllAsRead = async () => {
    if (actionInProgress) return;
    try {
      setActionInProgress(true);
      if (onMarkAllAsRead) {
        await onMarkAllAsRead();
        return;
      }
      setNotifications(notifications.map(notif => ({ ...notif, unread: false })));
    } finally {
      setActionInProgress(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    if (actionInProgress) return;
    try {
      setActionInProgress(true);
      if (onDeleteNotification) {
        await onDeleteNotification(notificationId);
        return;
      }
      setNotifications(notifications.filter(notif => notif.id !== notificationId));
    } finally {
      setActionInProgress(false);
    }
  };

  const clearAll = async () => {
    if (actionInProgress) return;
    if (window.confirm('Are you sure you want to clear all notifications?')) {
      try {
        setActionInProgress(true);
        if (onClearAll) {
          await onClearAll();
          return;
        }
        setNotifications([]);
      } finally {
        setActionInProgress(false);
      }
    }
  };

  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <div className="min-h-full pb-20 bg-gray-50">
      <div className={`p-6 ${isDesktop ? 'rounded-3xl' : 'rounded-b-3xl'}`} style={headerStyle}>
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            title="Go back"
            style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}
          >
            <ArrowLeft size={22} />
          </button>
          {unreadCount > 0 && (
            <span className="bg-white bg-opacity-20 text-white text-xs font-semibold px-3 py-1 rounded-full">
              {unreadCount} unread
            </span>
          )}
        </div>
        <h1 className="text-2xl font-bold">Notifications</h1>
        <p style={{ color: appTheme === 'white' ? '#888' : 'var(--accent-light)' }}>Stay updated with your orders and offers</p>
      </div>

      <div className="p-4">
        {notifications.length > 0 && (
          <div className="flex gap-2 mb-4">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={actionInProgress}
                className="flex-1 bg-orange-500 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-orange-600 transition-all"
              >
                Mark All Read
              </button>
            )}
            <button
              onClick={clearAll}
              disabled={actionInProgress}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all"
            >
              Clear All
            </button>
          </div>
        )}

        {notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No notifications</p>
            <p className="text-gray-400 text-sm mt-2">You're all caught up!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`border-2 rounded-xl p-4 transition-all ${
                  notification.unread
                    ? `${getNotificationColor(notification.type)} border-l-4`
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className={`font-semibold ${notification.unread ? 'text-gray-900' : 'text-gray-700'}`}>
                        {notification.title}
                      </h3>
                      {notification.unread && (
                        <span className="flex-shrink-0 w-2 h-2 bg-orange-500 rounded-full ml-2 mt-1"></span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{notification.message}</p>

                    {(notification.type === 'coupon' || notification.type === 'promotion') && notification.data?.coupon_code && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="flex-1 bg-white border border-dashed border-pink-400 rounded-lg px-3 py-1 text-sm font-mono font-semibold text-pink-700 tracking-widest">
                          {notification.data.coupon_code}
                        </span>
                        <button
                          onClick={() => copyCouponCode(notification.data.coupon_code)}
                          className="flex items-center gap-1 text-xs text-pink-600 hover:text-pink-700 font-medium"
                          title="Copy code"
                        >
                          <Copy size={14} />
                          {copiedCode === notification.data.coupon_code ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    )}

                    {notification.type === 'promotion' && notification.data?.discount_percentage && !notification.data?.coupon_code && (
                      <div className="mb-2">
                        <span className="inline-block bg-purple-100 text-purple-700 text-xs font-bold px-2 py-1 rounded-full">
                          {notification.data.discount_percentage}% OFF
                        </span>
                        {notification.data?.valid_until && (
                          <span className="ml-2 text-xs text-gray-400">
                            Valid until {new Date(notification.data.valid_until).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">{notification.time}</span>
                      <div className="flex gap-2 items-center">
                        {notification.type === 'order' && notification.data?.order_id && onViewOrder && (
                          <button
                            onClick={() => onViewOrder(notification.data.order_id, notification.data.order_number)}
                            className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600 font-medium"
                          >
                            <ExternalLink size={12} />
                            View Order
                          </button>
                        )}
                        {notification.unread && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            disabled={actionInProgress}
                            className="text-xs text-orange-500 hover:text-orange-600 font-medium"
                          >
                            Mark Read
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notification.id)}
                          disabled={actionInProgress}
                          className="text-xs text-red-600 hover:text-red-700 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {notifications.length > 0 && (
          <div className="mt-6 bg-orange-50 border border-orange-200 rounded-xl p-4">
            <p className="text-sm text-gray-700">
              💡 <span className="font-semibold">Tip:</span> Enable push notifications to get real-time updates about your orders and offers!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsModal;
